import { Box, Typography, Tooltip, useTheme, Theme } from "@material-ui/core";
import {
  Bluetooth,
  DeviceUnknown,
  Mail,
  Public,
  Usb,
  VerifiedUser,
  Visibility,
} from "@material-ui/icons";
import { DatePicker } from "@material-ui/pickers";
import { addDays } from "date-fns";
import LocalShippingOutlinedIcon from "@material-ui/icons/LocalShippingOutlined";
import ShowIcon from "@material-ui/icons/RemoveRedEye";
import {
  CreateButton,
  Datagrid,
  DateField,
  Filter,
  FunctionField,
  Record,
  sanitizeListRestProps,
  TextField,
  TextInput,
  TopToolbar,
  useListContext,
  Responsive,
  SimpleList,
} from "react-admin";
import permissionNames from "../../../common/auth/access-control/permissionNames";
import { useActionPermissions } from "../../../common/auth/useActionPermissions";
import {
  FmcsaInspectionListDto,
  FmcsaInspectionReportType,
} from "../../../common/data/service-proxies";
import { useResetListSearchField } from "../../../common/hooks/useResetListSearchField";
import { useSearchFields } from "../../../common/hooks/useSearchFields";
import { GenericDateOrTimePicker } from "../../common/form/GenericDateOrTimePicker";
import { DefaultProLogsList } from "../../common/list/DefaultProLogsList";
import { MarkFmcsaInspectionUnofficialMenuItem } from "./MarkFmcsaInspectionUnofficialMenuItem";
import { AppBarTenantSelector } from "../../common/data-grid-inputs/AppBarTenantSelector";
import { ActionsField } from "../../common/data-grid-fields/ActionsField";
import { ShowFmcsaInspectionMenuItem } from "./ShowFmcsaInspectionMenuItem";
import { useAppData } from "../../../common/hooks/useAppData";

const ListActions = (props: any) => {
  const { className, exporter, filters, maxResults, ...rest } = props;
  const { basePath } = useListContext();
  const permissions = useActionPermissions();
  return (
    <TopToolbar className={className} {...sanitizeListRestProps(rest)}>
      {permissions &&
        permissions[permissionNames.hos.fmcsaDataTransfer.main] && (
          <CreateButton basePath={basePath} />
        )}
    </TopToolbar>
  );
};

const ListFilter = (props: any) => {
  useSearchFields(props, ["driverFullName", "driverId", "vehicleName"]);
  useResetListSearchField(props);
  const { loading } = useListContext();
  return (
    <Filter {...props}>
      {loading && (
        <TextInput
          className="drivers-list-filter search-box"
          label="resources.common.labels.search"
          source="q"
          alwaysOn
          autoComplete="off"
        />
      )}
      {!loading && (
        <TextInput
          className="drivers-list-filter search-box"
          label="resources.common.labels.search"
          source="q"
          alwaysOn
          autoFocus
          autoComplete="off"
        />
      )}
      <GenericDateOrTimePicker
        PickerComponent={DatePicker}
        className="list-view-filter"
        label="resources.common.labels.dateFrom"
        source="dateFrom"
        alwaysOn
      />
      <GenericDateOrTimePicker
        PickerComponent={DatePicker}
        className="list-view-filter"
        label="resources.common.labels.dateTo"
        source="dateTo"
        alwaysOn
      />
    </Filter>
  );
};

export const FmcsaDataTransferList = (props: any) => {
  const theme: Theme = useTheme();
  const dateTo = new Date();
  const dateFrom = addDays(dateTo, -15);
  const permissions = useActionPermissions();
  const timeZone = useAppData((x) => x.timeZoneInfo);

  const renderPrimaryTextForSimpleList = (record: any) => {
    return (
      <div
        style={{
          display: "flex",
          flexDirection: "row",
          justifyContent: "space-between",
          gap: "10px",
          flexWrap: "wrap",
          alignItems: "center",
        }}
      >
        {record.driverId + " | " + record.driverFullName}
      </div>
    );
  };

  const renderSecondaryTextForSimpleList = (record: Record) => {
    return (
      <div
        style={{
          width: "100%",
          display: "flex",
          flexDirection: "column",
          gap: "10px",
        }}
      >
        <div
          style={{
            display: "flex",
            gap: "6px",
            alignItems: "center",
            marginTop: "14px",
            flexWrap: "wrap",
          }}
        >
          <Tooltip title="Vehicle">
            <div
              style={{
                display: "flex",
                gap: "6px",
                alignItems: "center",
                justifyContent: "center",
                color: theme.palette.text.secondary,
              }}
            >
              <LocalShippingOutlinedIcon
                style={{
                  color: theme.palette.success.main,
                }}
              />
              <TextField source="vehicleName" label="Vehicle" />
            </div>
          </Tooltip>
          <Tooltip title="Report type">
            <div
              style={{
                display: "flex",
                gap: "6px",
                alignItems: "center",
                justifyContent: "center",
                color: theme.palette.text.secondary,
              }}
            >
              <FunctionField<FmcsaInspectionListDto & Record>
                label="Report type"
                source="reportType"
                render={() => (
                  <Box className="mobile-app-container">
                    {record?.reportType ===
                    FmcsaInspectionReportType.Bluetooth ? (
                      <>
                        <Bluetooth
                          style={{
                            color: theme.palette.success.main,
                          }}
                        />
                        <Typography variant="body2">Bluetooth</Typography>
                      </>
                    ) : record?.reportType ===
                      FmcsaInspectionReportType.Email ? (
                      <>
                        <Mail
                          style={{
                            color: theme.palette.success.main,
                          }}
                        />
                        <Typography variant="body2">E-Mail</Typography>
                      </>
                    ) : record?.reportType ===
                      FmcsaInspectionReportType.TestEmail ? (
                      <>
                        <Mail
                          style={{
                            color: theme.palette.success.main,
                          }}
                        />
                        <Typography variant="body2">E-Mail (test)</Typography>
                      </>
                    ) : record?.reportType ===
                      FmcsaInspectionReportType.TestWeb ? (
                      <>
                        <Public
                          style={{
                            color: theme.palette.success.main,
                          }}
                        />
                        <Typography variant="body2">
                          Web service (test)
                        </Typography>
                      </>
                    ) : record?.reportType === FmcsaInspectionReportType.Usb ? (
                      <>
                        <Usb
                          style={{
                            color: theme.palette.success.main,
                          }}
                        />
                        <Typography variant="body2">USB</Typography>
                      </>
                    ) : record?.reportType === FmcsaInspectionReportType.Web ? (
                      <>
                        <Public
                          style={{
                            color: theme.palette.success.main,
                          }}
                        />
                        <Typography variant="body2">Web service</Typography>
                      </>
                    ) : (
                      <DeviceUnknown />
                    )}
                  </Box>
                )}
              />
            </div>
          </Tooltip>
        </div>
        <DateField
          style={{ color: theme.palette.text.secondary }}
          source="time"
          showTime
          label="Time"
          options={{ timeZone: timeZone?.ianaTimeZone }}
        />
      </div>
    );
  };

  return (
    <>
      {permissions &&
      permissions[permissionNames.hos.fmcsaDataTransfer.markUnofficial] ? (
        <AppBarTenantSelector />
      ) : null}
      <DefaultProLogsList
        className="content-list"
        title="resources.fmcsa-data-transfer.name"
        bulkActionButtons={false}
        actions={<ListActions />}
        filters={<ListFilter />}
        sort={{ field: "time", order: "DESC" }}
        filterDefaultValues={{ dateFrom, dateTo }}
        {...props}
      >
        <Responsive
          small={
            <SimpleList
              className="simple-list-wrapper"
              linkType="show"
              primaryText={renderPrimaryTextForSimpleList}
              secondaryText={renderSecondaryTextForSimpleList}
              rightIcon={() => (
                <ShowIcon
                  style={{
                    color: theme.palette.primary.main,
                  }}
                />
              )}
            />
          }
          medium={
            <Datagrid>
              <TextField source="driverDriverId" label="Driver ID" />
              <TextField source="driverFullName" label="Name" />
              <TextField source="vehicleName" label="Vehicle" />
              <DateField
                source="time"
                showTime
                label="Time"
                options={{ timeZone: timeZone?.ianaTimeZone }}
              />
              <FunctionField<FmcsaInspectionListDto & Record>
                label="Report type"
                source="reportType"
                render={(record) => (
                  <Box className="mobile-app-container">
                    {record?.reportType ===
                    FmcsaInspectionReportType.Bluetooth ? (
                      <>
                        <Bluetooth />
                        <Typography variant="body2">Bluetooth</Typography>
                      </>
                    ) : record?.reportType ===
                      FmcsaInspectionReportType.Email ? (
                      <>
                        <Mail />
                        <Typography variant="body2">E-Mail</Typography>
                      </>
                    ) : record?.reportType ===
                      FmcsaInspectionReportType.TestEmail ? (
                      <>
                        <Mail />
                        <Typography variant="body2">E-Mail (test)</Typography>
                      </>
                    ) : record?.reportType ===
                      FmcsaInspectionReportType.TestWeb ? (
                      <>
                        <Public />
                        <Typography variant="body2">
                          Web service (test)
                        </Typography>
                      </>
                    ) : record?.reportType === FmcsaInspectionReportType.Usb ? (
                      <>
                        <Usb />
                        <Typography variant="body2">USB</Typography>
                      </>
                    ) : record?.reportType === FmcsaInspectionReportType.Web ? (
                      <>
                        <Public />
                        <Typography variant="body2">Web service</Typography>
                      </>
                    ) : (
                      <DeviceUnknown />
                    )}
                  </Box>
                )}
              />
              <ActionsField>
                <ShowFmcsaInspectionMenuItem
                  label="View"
                  icon={<Visibility />}
                />
                {permissions &&
                permissions[
                  permissionNames.hos.fmcsaDataTransfer.markUnofficial
                ] ? (
                  <MarkFmcsaInspectionUnofficialMenuItem
                    hidden={(record) => !!record.isUnofficial}
                    label="Mark unofficial"
                    icon={<VerifiedUser />}
                  />
                ) : null}
              </ActionsField>
            </Datagrid>
          }
        />
      </DefaultProLogsList>
    </>
  );
};
